/* Shared header navigation. The search, account and cart links stay native Shopify links. */
(() => {
  'use strict';

  // Theme Editor can evaluate an asset again while replacing a section.
  const moduleKey = Symbol.for('seldravinpack.header.initialize');
  if (window[moduleKey]) {
    window[moduleKey](document);
    return;
  }

  const instances = new Map();
  const scrollOwners = new Set();
  let savedScroll = null;

  function lockScroll(owner) {
    if (scrollOwners.has(owner)) return;
    if (!scrollOwners.size) {
      const style = document.body.style;
      savedScroll = {
        x: window.scrollX,
        y: window.scrollY,
        properties: ['position', 'top', 'left', 'width', 'overflow'].map(name => [name, style.getPropertyValue(name), style.getPropertyPriority(name)])
      };
      style.setProperty('position', 'fixed');
      style.setProperty('top', `${-savedScroll.y}px`);
      style.setProperty('left', `${-savedScroll.x}px`);
      style.setProperty('width', '100%');
      style.setProperty('overflow', 'hidden');
    }
    scrollOwners.add(owner);
  }

  function unlockScroll(owner) {
    if (!scrollOwners.delete(owner) || scrollOwners.size || !savedScroll) return;
    const previous = savedScroll;
    savedScroll = null;
    previous.properties.forEach(([name, value, priority]) => {
      if (value) document.body.style.setProperty(name, value, priority);
      else document.body.style.removeProperty(name);
    });
    window.scrollTo({ left: previous.x, top: previous.y, behavior: 'instant' });
  }

  function createHeader(root) {
    const controller = new AbortController();
    const { signal } = controller;
    const nav = root.querySelector('[data-header-nav]');
    const toggle = root.querySelector('[data-menu-toggle]');
    const drawer = root.querySelector('[data-header-drawer]');
    const desktop = matchMedia('(min-width: 961px)');
    const hover = matchMedia('(hover: hover)');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const panels = [...root.querySelectorAll('.mega-panel')];
    const submenus = [...root.querySelectorAll('.drawer-submenu')];
    const items = [...root.querySelectorAll('.nav-inner [data-mega]')].map(trigger => ({
      trigger,
      panel: panels.find(panel => panel.id === trigger.getAttribute('aria-controls')),
      key: trigger.dataset.mega
    })).filter(item => item.panel);
    const accordions = [...root.querySelectorAll('[data-drawer-toggle]')].map(button => ({
      button,
      panel: submenus.find(panel => panel.id === button.getAttribute('aria-controls')),
      key: button.dataset.drawerToggle
    })).filter(item => item.panel);
    let active = null;
    let closeTimer;
    let focusTimer;
    let panelMotion = null;
    let drawerMotion = null;
    let closing = false;
    let motionVersion = 0;
    let suppressFocus = false;
    let destroyed = false;

    const listen = (target, type, handler, options = {}) => target?.addEventListener(type, handler, { ...options, signal });
    const stopCloseTimer = () => clearTimeout(closeTimer);

    function closeMega() {
      stopCloseTimer();
      panelMotion?.cancel();
      panelMotion = null;
      items.forEach(({ trigger, panel }) => {
        trigger.setAttribute('aria-expanded', 'false');
        panel.hidden = true;
      });
      active = null;
    }

    function openMega(item) {
      if (destroyed || !desktop.matches) return;
      stopCloseTimer();
      if (active === item && !item.panel.hidden) return;
      closeMega();
      active = item;
      item.trigger.setAttribute('aria-expanded', 'true');
      item.panel.hidden = false;
      if (!reducedMotion.matches) {
        panelMotion = item.panel.animate(
          [{ opacity: 0, transform: 'translateY(-5px)' }, { opacity: 1, transform: 'translateY(0)' }],
          { duration: 160, easing: 'ease-out' }
        );
      }
    }

    function setAccordion(selected, expanded) {
      accordions.forEach(item => {
        const open = item === selected && expanded;
        item.button.setAttribute('aria-expanded', String(open));
        item.panel.classList.toggle('expanded', open);
        item.panel.inert = !open;
      });
    }

    function syncToggle(open) {
      if (!toggle) return;
      toggle.setAttribute('aria-expanded', String(open));
      const label = open ? toggle.dataset.closeLabel : toggle.dataset.openLabel;
      if (label) toggle.setAttribute('aria-label', label);
    }

    function cancelDrawerMotion() {
      motionVersion += 1;
      drawerMotion?.cancel();
      drawerMotion = null;
    }

    function resetDrawer() {
      cancelDrawerMotion();
      closing = false;
      unlockScroll(root);
      syncToggle(false);
    }

    function openDrawer() {
      if (!drawer || destroyed || desktop.matches || (drawer.open && !closing)) return;
      const from = drawer.open ? getComputedStyle(drawer).transform : 'translateX(-100%)';
      cancelDrawerMotion();
      closing = false;
      closeMega();
      nav?.classList.remove('open');
      lockScroll(root);
      try {
        if (!drawer.open) drawer.showModal();
      } catch (error) {
        resetDrawer();
        return;
      }
      syncToggle(true);
      if (!reducedMotion.matches) {
        drawerMotion = drawer.animate(
          [{ transform: from }, { transform: 'translateX(0)' }],
          { duration: 260, easing: 'cubic-bezier(.22,.61,.36,1)' }
        );
      }
    }

    function closeDrawer(immediate = false, restoreFocus = true) {
      if (!drawer?.open) {
        resetDrawer();
        return;
      }
      if (closing && !immediate) return;
      const from = getComputedStyle(drawer).transform;
      cancelDrawerMotion();
      closing = true;
      const version = motionVersion;
      const finish = () => {
        if (version !== motionVersion) return;
        drawer.close();
        resetDrawer();
        if (restoreFocus && !desktop.matches && toggle?.isConnected) toggle.focus({ preventScroll: true });
      };
      if (immediate || reducedMotion.matches) {
        finish();
        return;
      }
      drawerMotion = drawer.animate(
        [{ transform: from }, { transform: 'translateX(-100%)' }],
        { duration: 220, easing: 'ease-in' }
      );
      drawerMotion.onfinish = finish;
    }

    items.forEach(item => {
      listen(item.trigger, 'pointerenter', () => { if (hover.matches) openMega(item); });
      listen(item.trigger, 'focus', () => {
        if (!suppressFocus && (hover.matches || item.trigger.matches(':focus-visible'))) openMega(item);
      });
      listen(item.trigger, 'click', event => {
        if (!desktop.matches) return;
        if (item.trigger.tagName === 'BUTTON' || (!hover.matches && active !== item)) {
          event.preventDefault();
          if (active === item) closeMega();
          else openMega(item);
        }
      });
      listen(item.trigger, 'keydown', event => {
        if (!desktop.matches || event.key !== 'ArrowDown') return;
        event.preventDefault();
        openMega(item);
        item.panel.querySelector('a[href], button:not([disabled]), input:not([disabled])')?.focus();
      });
      listen(item.panel, 'pointerenter', stopCloseTimer);
      listen(item.panel, 'click', event => {
        if (event.target.closest('a[href]')) closeMega();
      });
    });

    listen(nav, 'pointerenter', stopCloseTimer);
    listen(nav, 'pointerleave', () => {
      if (desktop.matches) closeTimer = setTimeout(closeMega, 160);
    });
    listen(nav, 'focusout', () => {
      clearTimeout(focusTimer);
      focusTimer = setTimeout(() => {
        if (!nav.contains(document.activeElement)) closeMega();
      }, 0);
    });
    listen(document, 'pointerdown', event => {
      if (!nav?.contains(event.target)) closeMega();
    });
    listen(document, 'keydown', event => {
      if (event.key !== 'Escape' || !active) return;
      const previous = active.trigger;
      event.preventDefault();
      closeMega();
      suppressFocus = true;
      previous.focus({ preventScroll: true });
      suppressFocus = false;
    });

    listen(toggle, 'click', event => {
      if (desktop.matches || !drawer) return;
      event.preventDefault();
      if (drawer.open && !closing) closeDrawer();
      else openDrawer();
    });
    listen(drawer?.querySelector('.drawer-close'), 'click', () => closeDrawer());
    listen(drawer, 'cancel', event => { event.preventDefault(); closeDrawer(); });
    listen(drawer, 'close', () => { if (!drawer.open) resetDrawer(); });
    listen(drawer, 'click', event => {
      if (event.target.closest('a[href]')) {
        closeDrawer(true, false);
      } else if (event.target === drawer) {
        const bounds = drawer.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeDrawer();
      }
    });
    accordions.forEach(item => {
      listen(item.button, 'click', () => setAccordion(item, item.button.getAttribute('aria-expanded') !== 'true'));
    });
    listen(desktop, 'change', () => {
      closeMega();
      closeDrawer(true, false);
      setAccordion(null, false);
    });
    listen(reducedMotion, 'change', () => {
      if (!reducedMotion.matches) return;
      panelMotion?.cancel();
      if (closing) closeDrawer(true);
      else cancelDrawerMotion();
    });

    listen(root, 'shopify:block:select', event => {
      let block = event.target;
      if (event.detail?.blockId) {
        const id = String(event.detail.blockId);
        const matched = [...root.querySelectorAll('[data-shopify-editor-block], [data-block-id]')].find(element => {
          if (element.dataset.blockId === id) return true;
          try { return JSON.parse(element.dataset.shopifyEditorBlock || '{}').id === id; }
          catch { return false; }
        });
        block = matched || block;
      }
      const panel = block.closest('.mega-panel');
      const category = block.closest('.drawer-category');
      const item = items.find(candidate => candidate.panel === panel);
      const accordion = accordions.find(candidate => category?.contains(candidate.button) || (item && candidate.key === item.key));
      if (desktop.matches) {
        const target = item || items.find(candidate => accordion && candidate.key === accordion.key);
        if (target) openMega(target);
      } else if (accordion) {
        openDrawer();
        setAccordion(accordion, true);
        accordion.button.scrollIntoView({ block: 'nearest', behavior: 'instant' });
      }
    });

    closeMega();
    setAccordion(null, false);
    syncToggle(false);
    return {
      destroy() {
        destroyed = true;
        clearTimeout(closeTimer);
        clearTimeout(focusTimer);
        controller.abort();
        closeMega();
        closeDrawer(true, false);
        setAccordion(null, false);
      }
    };
  }

  function initialize(scope) {
    const roots = [...scope.querySelectorAll('[data-shopify-header]')];
    if (scope.matches?.('[data-shopify-header]')) roots.unshift(scope);
    roots.forEach(root => {
      if (!instances.has(root)) instances.set(root, createHeader(root));
    });
  }

  function removeHeader(root) {
    instances.get(root)?.destroy();
    instances.delete(root);
  }

  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    [...instances.keys()].forEach(root => {
      if (event.target === root || event.target.contains(root)) removeHeader(root);
    });
  });
  new MutationObserver(() => {
    [...instances.keys()].forEach(root => { if (!root.isConnected) removeHeader(root); });
  }).observe(document.documentElement, { childList: true, subtree: true });

  window[moduleKey] = initialize;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  else initialize(document);
})();
